import { useEffect, useState } from "react";
import { Button, Modal, Toast, type ToastType } from "@legal-500/legal500-design-system";
import {
  POWERLIST_DELETE_PERMISSION,
  POWERLIST_PUBLISH_PERMISSION,
  POWERLIST_WRITE_PERMISSION,
} from "../../constants/permissions";
import { usePermissions } from "../../hooks/usePermissions";
import styles from "./Powerlist.module.css";

export function usePowerlistAccess() {
  const { hasPermission } = usePermissions();
  return {
    canWrite: hasPermission(POWERLIST_WRITE_PERMISSION),
    canPublish: hasPermission(POWERLIST_PUBLISH_PERMISSION),
    canDelete: hasPermission(POWERLIST_DELETE_PERMISSION),
  };
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function textOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

export function parseSortOrder(value: string): number | undefined | "invalid" {
  if (value.trim() === "") return undefined;
  return /^\d+$/.test(value.trim()) ? Number(value) : "invalid";
}

export type ToastState = { message: string; type: ToastType } | null;

export function useToast() {
  const [toast, setToast] = useState<ToastState>(null);
  const element = toast && (
    <Toast
      message={toast.message}
      type={toast.type}
      duration={toast.type === "error" ? 5000 : 3000}
      onClose={() => setToast(null)}
    />
  );
  return {
    toast: element,
    success: (message: string) => setToast({ message, type: "success" }),
    failure: (error: unknown) => setToast({ message: errorMessage(error), type: "error" }),
  };
}

type ConfirmModalProps = {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
};

export function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel,
  onConfirm,
  onClose,
}: ConfirmModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setBusy(false);
    }
  }, [isOpen]);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (confirmError) {
      setError(errorMessage(confirmError));
      setBusy(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="small">
      <div className={styles.form}>
        {error && (
          <div className={styles.formError} role="alert">
            {error}
          </div>
        )}
        <p>{message}</p>
        <div className={styles.formActions}>
          <Button variant="outlined" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={confirm} disabled={busy}>
            {busy ? "Working..." : confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className={styles.formError} role="alert">
      {message}
    </div>
  );
}
